import { zodResolver } from '@hookform/resolvers/zod'
import { parseTime, Time } from '@internationalized/date'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { z } from 'zod'

import { ApiBusinessDayResponse } from '@/ducks/account/account.schema'
import { ApiGetBusinessHours } from '@/ducks/account/account.types'
import {
  useUpdateBranchBusinessHoursMutation,
  useUpdateBusinessHoursMutation
} from '@/ducks/account/api/account.api'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'
import {
  selectSelectedBranchId,
  selectStoreId
} from '@/ducks/store/selectors/store.selectors'
import { capitalize } from '@/utils/string.utils'
import { pushGtmFormSubmitEvent } from '@/utils/gtm.utils'
import {
  getLocalTime,
  getTimestampFromTime,
  sanitizeTimeZone,
  WEEKDAYS
} from '@/utils/time.utils'

const WeekdaySchema = z.object({
  day_name: z.enum(WEEKDAYS),
  start_time: z.string().nullable(),
  end_time: z.string().nullable(),
  isWorking: z.boolean(),
  slots: z.array(
    z.object({
      start_time: z.string().nullable(),
      end_time: z.string().nullable()
    })
  )
})
type WeekdayValue = z.infer<typeof WeekdaySchema>

const formSchema = z.object({
  businessHours: z.array(WeekdaySchema)
})
type FormData = z.infer<typeof formSchema>
type ErrorHours = z.infer<typeof ApiBusinessDayResponse>
const mapApiToForm = (data: ApiGetBusinessHours) => {
  const { timezone, businessHours } = data
  return {
    businessHours: businessHours.map(weekday => ({
      ...weekday,
      start_time: getLocalTime(weekday.start_time, sanitizeTimeZone(timezone)),
      end_time: getLocalTime(weekday.end_time, sanitizeTimeZone(timezone)),
      slots: weekday.slots.map(slot => ({
        start_time: getLocalTime(slot.start_time, sanitizeTimeZone(timezone)),
        end_time: getLocalTime(slot.end_time, sanitizeTimeZone(timezone))
      }))
    }))
  }
}

const mapFormToApi = (data: FormData, timezone: string) => {
  const { businessHours } = data
  const tz = sanitizeTimeZone(timezone)
  return {
    businessHours: businessHours.map(weekday => ({
      ...weekday,
      start_time: getTimestampFromTime(weekday.start_time, tz),
      end_time: getTimestampFromTime(weekday.end_time, tz),
      slots: weekday.slots.map(slot => ({
        start_time: getTimestampFromTime(slot.start_time, tz),
        end_time: getTimestampFromTime(slot.end_time, tz)
      }))
    }))
  }
}

const useBusinessHoursForm = (initialData: ApiGetBusinessHours) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const location = useLocation()
  const isBranchBusinessHours = location.pathname.includes(
    '/branch/business-hours'
  )
  const storeId = useSelector(selectStoreId) || ''
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [errorHours, setErrorHours] = useState<object | null>(null)
  const [updateBranchBusinessHours] = useUpdateBranchBusinessHoursMutation()
  const selectedBranchId = useSelector(selectSelectedBranchId)

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: mapApiToForm(initialData)
  })

  const slots = form.watch(
    WEEKDAYS.map((_, index) => `businessHours.${index}.slots` as const)
  )

  const weekdays = form.watch(
    WEEKDAYS.map((_, index) => `businessHours.${index}` as const)
  )

  const handleSubmit = form.handleSubmit(async values => {
    pushGtmFormSubmitEvent()
    const activeId = isBranchBusinessHours ? selectedBranchId : storeId
    setErrorMessage(null)
    setErrorHours(null)
    const DTO = mapFormToApi(values, initialData.timezone)
    return updateBranchBusinessHours({ storeId: activeId, ...DTO })
      .unwrap()
      .then(({ message }) => {
        if (message === 'success') {
          dispatch(
            addSystemNotification({
              message: t('account.business-hours.success'),
              intent: 'success',
              duration: 3000
            })
          )
          form.reset(values)
        } else {
          throw Error(message)
        }
      })
      .catch(err => {
        // Format city hours in store/city timezone (backend validates in city TZ)
        if (err?.cityBusinessHours?.length) {
          const tz = sanitizeTimeZone(initialData.timezone)
          const formatInTz = (ms: number) =>
            new Date(ms).toLocaleTimeString([], {
              timeZone: tz,
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            })
          const transformedArray = err.cityBusinessHours.map(
            (item: ErrorHours) =>
              t('account.business-hours.invalid.description', {
                day_name: capitalize(item.day_name),
                start_time: formatInTz(item.start_time),
                end_time: formatInTz(item.end_time)
              })
          )
          setErrorMessage(t('account.business-hours.error'))
          setErrorHours(transformedArray)
        }
        dispatch(
          addSystemNotification({
            message: t('account.business-hours.error'),
            intent: 'error',
            duration: 3000
          })
        )
      })
  })

  const handleAddSlot = useCallback(
    (index: number) => {
      const end = form.getValues(`businessHours.${index}.end_time`)
      const endTime = end ? parseTime(end) : null

      form.setValue(
        `businessHours.${index}.slots`,
        [
          {
            start_time: endTime
              ? endTime
                  .add({ hours: 1 })
                  .toString()
                  .split(':')
                  .slice(0, 2)
                  .join(':')
              : null,
            end_time: '23:59'
          }
        ],
        { shouldDirty: true }
      )
    },
    [form]
  )

  const handleDeleteSlot = useCallback(
    (index: number) => {
      form.setValue(`businessHours.${index}.slots.0.start_time`, null, {
        shouldDirty: true
      })
      form.setValue(`businessHours.${index}.slots.0.end_time`, null, {
        shouldDirty: true
      })
      form.setValue(`businessHours.${index}.slots`, [])
    },
    [form]
  )
  const hasAdditionalSlot = useCallback(
    (index: number) => slots[index].length > 0,
    [slots]
  )

  const getFormattedValue = useCallback(
    (weekdayValue: WeekdayValue) => {
      const firstSlotFormattedValue =
        weekdayValue.start_time && weekdayValue.end_time
          ? `${weekdayValue.start_time}-${weekdayValue.end_time}`
          : t('account.business-hours.not-set')

      const secondSlotFormattedValue =
        weekdayValue.slots.length > 0
          ? weekdayValue.slots[0].start_time && weekdayValue.slots[0].end_time
            ? `${weekdayValue.slots[0].start_time}-${weekdayValue.slots[0].end_time}`
            : t('account.business-hours.not-set')
          : null

      return secondSlotFormattedValue
        ? [firstSlotFormattedValue, secondSlotFormattedValue].join(' / ')
        : firstSlotFormattedValue
    },
    [t]
  )

  const shouldDisableAdd = useCallback(
    (index: number) => {
      const end = weekdays[index].end_time
      if (!end) return false

      const endTime = parseTime(end)

      return endTime.compare(new Time(23, 57)) >= 0
    },
    [weekdays]
  )
  const getMixMaxRules = useCallback(
    (index: number, fieldByLetter: 'A' | 'B' | 'C' | 'D') => {
      const weekdayValue = form.getValues(`businessHours.${index}`)
      const { end_time, start_time } = weekdayValue

      const hasSlots = weekdayValue.slots.length > 0
      const slot_start_time = weekdayValue.slots?.[0]?.start_time || null
      const slot_end_time = weekdayValue.slots?.[0]?.end_time || null

      switch (fieldByLetter) {
        case 'A':
          return {
            min: null,
            max: end_time
          }
        case 'B':
          return {
            min: start_time,
            max: hasSlots ? slot_start_time || '23:01' : null
          }
        case 'C':
          return {
            min: end_time,
            max: slot_end_time ? slot_end_time : '23:30'
          }
        case 'D':
          return {
            min: slot_start_time,
            max: null
          }
        default:
          return null as never
      }
    },
    [form]
  )

  return {
    form,
    handleAddSlot,
    handleDeleteSlot,
    hasAdditionalSlot,
    getFormattedValue,
    handleSubmit,
    shouldDisableAdd,
    getMixMaxRules,
    errorMessage,
    errorHours
  } as const
}

export { useBusinessHoursForm }
